import { useEffect, useState } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { apiFetch } from "@/lib/api-fetch";

const STORAGE_KEY = "cd_intake_count";
const EVENT_NAME = "cd-intake-count";
const POLL_MS = 60_000;

function readStored(): number {
  if (typeof window === "undefined") return 0;
  const raw = window.localStorage.getItem(STORAGE_KEY);
  const n = raw ? parseInt(raw, 10) : 0;
  return Number.isFinite(n) ? n : 0;
}

function writeStored(count: number) {
  try {
    window.localStorage.setItem(STORAGE_KEY, String(count));
    window.dispatchEvent(new CustomEvent(EVENT_NAME, { detail: count }));
  } catch {
    /* ignore */
  }
}

export function useIntakeQueueCount(enabled: boolean): number {
  const { token } = useAuth();
  const [count, setCount] = useState<number>(() => readStored());

  useEffect(() => {
    if (!enabled || !token) return;

    let cancelled = false;
    async function fetchCount() {
      try {
        const res = await apiFetch("/api/ops/intake-queue", {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!res.ok) return;
        const data = await res.json();
        const n = (data.orders ?? []).length;
        if (!cancelled) {
          setCount(n);
          writeStored(n);
        }
      } catch {
        /* ignore */
      }
    }
    fetchCount();
    const t = setInterval(fetchCount, POLL_MS);
    return () => {
      cancelled = true;
      clearInterval(t);
    };
  }, [enabled, token]);

  useEffect(() => {
    function onEvent(e: Event) {
      const detail = (e as CustomEvent<number>).detail;
      if (typeof detail === "number") setCount(detail);
    }
    function onStorage(e: StorageEvent) {
      if (e.key === STORAGE_KEY && e.newValue) {
        const n = parseInt(e.newValue, 10);
        if (Number.isFinite(n)) setCount(n);
      }
    }
    window.addEventListener(EVENT_NAME, onEvent as EventListener);
    window.addEventListener("storage", onStorage);
    return () => {
      window.removeEventListener(EVENT_NAME, onEvent as EventListener);
      window.removeEventListener("storage", onStorage);
    };
  }, []);

  return count;
}
